import { Injectable, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  AdminInvoiceListItem,
  GenerateBookingInvoicesResult,
  InvoiceType,
  RepairMissingInvoicesResult,
} from '../models/invoice.model';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';

@Injectable({ providedIn: 'root' })
export class AdminInvoiceService extends FirebaseAwareService {
  private readonly functions = inject(Functions);

  async listInvoices(limit = 50): Promise<AdminInvoiceListItem[]> {
    return this.inCtx(async () => {
      const call = httpsCallable<{ limit?: number }, { invoices: AdminInvoiceListItem[] }>(
        this.functions,
        'listInvoicesAdmin',
      );
      const res = await call({ limit });
      return res.data.invoices;
    });
  }

  async getInvoiceHtml(invoiceId: string): Promise<{ invoiceNumber: string; html: string }> {
    return this.inCtx(async () => {
      const call = httpsCallable<{ invoiceId: string }, { invoiceNumber: string; html: string }>(
        this.functions,
        'getInvoiceHtmlAdmin',
      );
      const res = await call({ invoiceId });
      return res.data;
    });
  }

  async generateBookingInvoices(
    bookingId: string,
    types?: InvoiceType[],
  ): Promise<GenerateBookingInvoicesResult> {
    return this.inCtx(async () => {
      const call = httpsCallable<
        { bookingId: string; types?: InvoiceType[] },
        GenerateBookingInvoicesResult
      >(this.functions, 'generateBookingInvoicesAdmin');
      const res = await call({ bookingId, ...(types ? { types } : {}) });
      return res.data;
    });
  }

  /** One-shot: stamps ownerUid on historical invoices so owners can see them. */
  async backfillOwnerUids(): Promise<{ scanned: number; updated: number }> {
    return this.inCtx(async () => {
      const call = httpsCallable<void, { scanned: number; updated: number }>(
        this.functions,
        'backfillInvoiceOwnerUids',
      );
      const res = await call();
      return res.data;
    });
  }

  /** Repairs recent paid bookings whose mandatory commission invoice is missing. */
  async repairMissingInvoices(limit = 200): Promise<RepairMissingInvoicesResult> {
    return this.inCtx(async () => {
      const call = httpsCallable<{ limit?: number }, RepairMissingInvoicesResult>(
        this.functions,
        'repairMissingBookingInvoicesAdmin',
      );
      const res = await call({ limit });
      return res.data;
    });
  }

  /** Opens stored HTML in a new tab for printing. */
  openPrintWindow(html: string, title: string): void {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.document.title = title;
      win.onload = () => URL.revokeObjectURL(url);
    }
  }
}
